import {
  ActiveStreamingSession,
  getActiveStreamingSession,
  isSessionInFlight,
} from './streamingService';

/**
 * ONE `/streaming/active_session` poll per patient, shared by every consumer.
 *
 * Three independent pollers used to ask the same question about the same patient
 * on the same screen:
 *
 *   ActivePlanCaseSelector   10s  — patient header, so EVERY patient tab
 *   useActiveSessionGuard    10s  — plan editor
 *   usePullingPatientResults 3s live / 30s idle — every patient tab
 *
 * On the plan editor that is 14 requests/minute idle and 32 while a session is
 * live, for one boolean. None of them knew about the others.
 *
 * Consolidating is safe because the poll is ADVISORY UI only: both gating
 * consumers re-check with `getActiveStreamingSession` immediately before they act
 * (`ActivePlanCaseSelector`'s pre-flight before the PATCH, and the guard's
 * `checkNow`), so a stale tick can delay a control greying out but can never let
 * a write through against a live session.
 *
 * Cadence is adaptive and matches the FASTEST any previous consumer used, so no
 * consumer is degraded: 3s while a session is in flight (was only the results
 * poller's rate), 10s otherwise (was the two gating pollers' rate). The results
 * poller idled at 30s, so it now sees more ticks — but it only refetches results
 * when a session is live, so the extra ticks cost nothing beyond the shared probe
 * it is already sharing.
 */

export const LIVE_POLL_INTERVAL_MS = 3000;
export const IDLE_POLL_INTERVAL_MS = 10000;

// Bound the probe. Consolidating three loops into one concentrates the risk: a
// hung request used to stall one consumer, now it would stall all of them. Kept
// under the idle interval so a dead socket cannot make ticks overlap.
export const PROBE_TIMEOUT_MS = 8000;

export type ActiveSessionSnapshot = {
  session: ActiveStreamingSession | undefined;
  inFlight: boolean;
};

type Listener = (snapshot: ActiveSessionSnapshot) => void;

type PollerState = {
  listeners: Set<Listener>;
  timer: ReturnType<typeof setTimeout> | undefined;
  stopped: boolean;
  last: ActiveSessionSnapshot | undefined;
};

const pollers = new Map<string, PollerState>();

async function tick(patientId: string, state: PollerState) {
  if (state.stopped) {
    return;
  }
  let snapshot: ActiveSessionSnapshot;
  try {
    const session = await getActiveStreamingSession(patientId, PROBE_TIMEOUT_MS);
    snapshot = { session, inFlight: isSessionInFlight(session) };
  } catch {
    // A probe failure must not change anyone's mind: every previous poller
    // treated an error as "leave the previous flag in place". Re-broadcasting the
    // last known snapshot preserves that, and keeps the loop alive.
    snapshot = state.last ?? { session: undefined, inFlight: false };
  }
  if (state.stopped) {
    return;
  }
  state.last = snapshot;
  // A listener that throws must not kill the loop for the others.
  state.listeners.forEach((listener) => {
    try {
      listener(snapshot);
    } catch {
      // ignored on purpose — see above
    }
  });
  // Scheduling inline rather than via a helper: the two would be mutually
  // recursive, which trips no-use-before-define whichever order they are written.
  if (state.stopped || state.listeners.size === 0) {
    return;
  }
  state.timer = setTimeout(
    () => void tick(patientId, state),
    snapshot.inFlight ? LIVE_POLL_INTERVAL_MS : IDLE_POLL_INTERVAL_MS,
  );
}

/**
 * Subscribe to this patient's session state. Returns an unsubscribe function.
 * The first subscriber starts the poll; the last one to leave stops it.
 */
export function subscribeToActiveSession(patientId: string, listener: Listener): () => void {
  let state = pollers.get(patientId);
  if (!state) {
    state = {
      listeners: new Set(),
      timer: undefined,
      stopped: false,
      last: undefined,
    };
    pollers.set(patientId, state);
  }
  const poller = state;
  poller.listeners.add(listener);

  // A late subscriber gets the current answer immediately rather than waiting a
  // whole interval — this is what preserves each old poller's "check() before
  // starting the interval" behaviour.
  if (poller.last) {
    try {
      listener(poller.last);
    } catch {
      // ignored on purpose
    }
  }

  const isFirst = poller.listeners.size === 1;
  if (isFirst) {
    void tick(patientId, poller);
  }

  return () => {
    poller.listeners.delete(listener);
    if (poller.listeners.size === 0) {
      poller.stopped = true;
      if (poller.timer) {
        clearTimeout(poller.timer);
      }
      pollers.delete(patientId);
    }
  };
}

/** Test seam — drops all pollers. */
export function resetActiveSessionPollersForTest() {
  pollers.forEach((state) => {
    state.stopped = true;
    if (state.timer) {
      clearTimeout(state.timer);
    }
  });
  pollers.clear();
}
